import { PUZZLE_PENDING_GUESSES_KEY } from "@/constants/puzzle-storage.constants";

const GUESSED_PROPERTY_RESULTS_KEY = "guessed_property_results";
const GUESSED_PROPERTIES_KEY = "guessed_properties";

/** Matches server `PendingPuzzleGuessDto` for POST /api/users/find-or-create (body field `pendingGuesses`). */
export type PendingPuzzleGuessForApi = {
    shortLink: string;
    guesses: number[];
    isArchivePlay?: boolean;
};

const MAX_PENDING_PUZZLE_GUESSES = 30;
const MAX_GUESSES_PER_PUZZLE = 6;

type PendingMap = Record<string, PendingPuzzleGuessForApi>;

/** When localStorage is unavailable, only the latest guest completion is retained (in-memory). */
type MemoryPendingWrap = {
    playablePropertyId: string;
    payload: PendingPuzzleGuessForApi;
};

let memoryPendingLastPuzzle: MemoryPendingWrap | null = null;

function isValidPendingEntry(value: unknown): value is PendingPuzzleGuessForApi {
    if (!value || typeof value !== "object") return false;
    const row = value as Record<string, unknown>;
    const shortLink = row.shortLink;
    const guesses = row.guesses;
    if (typeof shortLink !== "string" || !shortLink.trim()) return false;
    if (!Array.isArray(guesses) || guesses.length === 0) return false;
    return true;
}

/** Existing queued guess for one playable property (used to avoid clobbering a valid shortLink on Save CTA). */
export function readPendingGuestGuessForPlayable(playablePropertyId: string): PendingPuzzleGuessForApi | null {
    const playableId = String(playablePropertyId).trim();
    if (!playableId) {
        return null;
    }
    const map = readPendingMapFromLocalStorage();
    if (memoryPendingLastPuzzle?.playablePropertyId === playableId) {
        return memoryPendingLastPuzzle.payload;
    }
    const entry = map[playableId];
    return isValidPendingEntry(entry) ? entry : null;
}

function readPendingMapFromLocalStorage(): PendingMap {
    try {
        const raw = localStorage.getItem(PUZZLE_PENDING_GUESSES_KEY);
        if (!raw) return {};
        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
        const out: PendingMap = {};
        for (const [playableId, value] of Object.entries(parsed as Record<string, unknown>)) {
            if (!playableId || !isValidPendingEntry(value)) continue;
            const guesses = (value as PendingPuzzleGuessForApi).guesses
                .map((g) => parseInt(String(g).replace(/\D/g, ""), 10))
                .filter((n) => Number.isFinite(n) && n > 0)
                .slice(0, MAX_GUESSES_PER_PUZZLE);
            if (guesses.length === 0) continue;
            const src = value as PendingPuzzleGuessForApi;
            out[playableId] = {
                shortLink: String(src.shortLink).trim(),
                guesses,
                ...(src.isArchivePlay === true ? { isArchivePlay: true } : {}),
            };
        }
        return out;
    } catch {
        return {};
    }
}

function trimMapToMaxKeys(map: PendingMap, maxKeys: number): PendingMap {
    const keys = Object.keys(map);
    if (keys.length <= maxKeys) return map;
    const dropCount = keys.length - maxKeys;
    const next: PendingMap = { ...map };
    for (let i = 0; i < dropCount; i++) {
        delete next[keys[i]];
    }
    return next;
}

/**
 * Persists one guest puzzle completion under {@link PUZZLE_PENDING_GUESSES_KEY}, keyed by playable property id.
 * If localStorage throws, stores only the latest puzzle in memory for registration.
 */
export function persistGuestPuzzleCompletionToPendingStore(params: {
    playablePropertyId: string | number | null | undefined;
    shortLink: string | number | null | undefined;
    guesses: number[];
    isArchivePlay: boolean;
}): void {
    const playableId = params.playablePropertyId != null ? String(params.playablePropertyId).trim() : "";
    if (!playableId) {
        return;
    }
    const shortLinkRaw = params.shortLink != null ? String(params.shortLink).trim() : "";
    if (!shortLinkRaw) {
        return;
    }
    const guesses = params.guesses
        .filter((n) => Number.isFinite(n) && n > 0)
        .map((n) => Math.round(Number(n)))
        .slice(0, MAX_GUESSES_PER_PUZZLE);
    if (guesses.length === 0) {
        return;
    }
    const payload: PendingPuzzleGuessForApi = {
        shortLink: shortLinkRaw,
        guesses,
        ...(params.isArchivePlay ? { isArchivePlay: true } : {}),
    };
    try {
        let map = readPendingMapFromLocalStorage();
        if (memoryPendingLastPuzzle) {
            map = { ...map, [memoryPendingLastPuzzle.playablePropertyId]: memoryPendingLastPuzzle.payload };
            memoryPendingLastPuzzle = null;
        }
        map[playableId] = payload;
        map = trimMapToMaxKeys(map, MAX_PENDING_PUZZLE_GUESSES);
        localStorage.setItem(PUZZLE_PENDING_GUESSES_KEY, JSON.stringify(map));
    } catch {
        memoryPendingLastPuzzle = { playablePropertyId: playableId, payload };
    }
}

/**
 * Returns all pending guest guesses for POST /api/users/find-or-create (`pendingGuesses`), from localStorage and any in-memory fallback.
 */
export function getAllPendingGuessesForRegistration(): PendingPuzzleGuessForApi[] {
    const map = readPendingMapFromLocalStorage();
    if (memoryPendingLastPuzzle) {
        map[memoryPendingLastPuzzle.playablePropertyId] = memoryPendingLastPuzzle.payload;
    }
    const list = Object.values(map);
    if (list.length <= MAX_PENDING_PUZZLE_GUESSES) {
        return list;
    }
    const keys = Object.keys(map);
    const start = keys.length - MAX_PENDING_PUZZLE_GUESSES;
    return keys.slice(start).map((k) => map[k]);
}

/** Clears {@link PUZZLE_PENDING_GUESSES_KEY} and the in-memory fallback after a successful import. */
export function clearPuzzlePendingGuessesStorage(): void {
    memoryPendingLastPuzzle = null;
    try {
        localStorage.removeItem(PUZZLE_PENDING_GUESSES_KEY);
    } catch {
        /* ignore */
    }
}

/**
 * Clears pending guest guess queues and legacy guest result keys after find-or-create succeeds with imports.
 */
export function clearGuestPuzzleLocalStorageAfterImport(): void {
    clearPuzzlePendingGuessesStorage();
    try {
        localStorage.setItem(GUESSED_PROPERTY_RESULTS_KEY, JSON.stringify([]));
        localStorage.setItem(GUESSED_PROPERTIES_KEY, JSON.stringify([]));
    } catch {
        /* ignore */
    }
}
